import { css } from '@emotion/css';
import { tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Checkbox, Flex, Input, Tabs, theme } from 'antd';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useKeyAction } from '../../../../hooks/useKeyAction.ts';
import { useLabResultViewStore, type ValuesFilter } from '../../../../store/useLabResultViewStore.ts';

const useObservationResultFilterClassNames = () => {
  const { token } = theme.useToken();

  return {
    layoutClassName: css`
      flex-wrap: wrap;
      width: 100%;
      gap: 0 ${token.paddingLG}px;
      justify-items: right;
      margin-top: ${token.margin}px;
    `,
    inputClassName: css`
      margin: 8px 8px 8px 0;
      min-width: 74px;
    `,
    tabsClassName: css`
      flex-grow: 1;
      overflow: hidden;
    `,
    filterCurrentOnly: css`
      margin-bottom: ${token.margin}px;
    `,
  };
};

function getTabItems(values: number, pathological: number) {
  const items = [];
  if (values > 0) {
    items.push({ key: 'all', label: `Alle Werte (${values})` });
    if (pathological > 0) {
      items.push({ key: 'patho', label: `pathologisch (${pathological})` });
      const nonPathoCount = values - pathological;
      if (nonPathoCount > 0) {
        items.push({ key: 'non-patho', label: `nicht pathologisch (${nonPathoCount})` });
      }
    }
  }
  return items;
}

export const CumulativeLabResultTabAndFilter: React.FC = () => {
  const tabState = useLabResultViewStore(state => state.tabState);
  const setTab = useLabResultViewStore(state => state.setTab);

  const tokens = useLabResultViewStore(state => state.tokens);
  const setTokens = useLabResultViewStore(state => state.setTokens);

  const selectedCumulativeGroups = useLabResultViewStore(state => state.selectedCumulativeGroups);

  const currentOnly = useLabResultViewStore(state => state.currentOnly);
  const switchCurrentOnly = useLabResultViewStore(state => state.switchCurrentOnly);

  const [filterTokens, setFilterTokens] = useState(() => tokens.join(' '));
  const [focused, setFocused] = useState(false);
  const [key, setKey] = useState<string>(() => (tabState.type === 'VALUE' ? (tabState.tab as ValuesFilter) : 'all'));

  const styles = useObservationResultFilterClassNames();

  useEffect(() => {
    setTokens(tokenize(filterTokens));
  }, [setTokens, filterTokens]);

  useEffect(() => {
    setTab('VALUE', key === 'all' ? 'ALL' : key === 'patho' ? 'PATHOLOGICAL' : 'NONE_PATHOLOGICAL');
  }, [setTab, key]);

  const action = useMemo(() => {
    if (focused) {
      return () => {
        setFilterTokens('');
      };
    } else {
      return null;
    }
  }, [focused]);

  useKeyAction('Escape', action);

  const items = useMemo(() => {
    let values = 0;
    let pathological = 0;

    selectedCumulativeGroups.forEach(g => {
      g.parameters.forEach(p => {
        values++;
        if (p.results.some(r => !!r?.pathological)) {
          pathological++;
        }
      });
    });

    return getTabItems(values, pathological);
  }, [selectedCumulativeGroups]);

  if (items.length === 0) {
    return undefined;
  }

  return (
    <Flex vertical>
      <Flex className={styles.layoutClassName}>
        <Tabs className={styles.tabsClassName} size="small" activeKey={key} onChange={setKey} items={items} />
        <div className={styles.inputClassName}>
          <Input
            placeholder="Filter"
            value={filterTokens}
            onChange={e => setFilterTokens(e.target.value)}
            allowClear
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </div>
      </Flex>
      <Checkbox checked={currentOnly} onClick={() => switchCurrentOnly()} className={styles.filterCurrentOnly}>
        nur Untersuchungen aus dem aktuellen Befund anzeigen
      </Checkbox>
    </Flex>
  );
};
